import { z } from 'zod';
import { SIDES } from '../constants';
import type {
  AtsResult,
  Favorite,
  FootballLevel,
  PickResult,
  SelectionSource,
  Side,
  SlateGameStatus,
} from '../constants';
import type { LockReason } from '../domain/lock';
import type { SeasonConfigDto, WeekSummaryDto } from './auth';

export const setPickBody = z.object({ selection: z.enum(SIDES) });
export type SetPickBody = z.infer<typeof setPickBody>;

export interface TeamDto {
  id: string;
  level: FootballLevel;
  /** `Oklahoma State` */
  name: string;
  /** `Oklahoma St` — for tight mobile rows */
  shortName: string;
  /** `OKST` */
  abbreviation: string;
  /** `Stillwater, OK` */
  location: string;
  /** `Cowboys` */
  nickname: string;
  conference: string | null;
  primaryColor: string | null;
  secondaryColor: string | null;
  logoUrl: string | null;
}

/**
 * One tappable side of a contest. The API hands the UI a pre-signed line and a formatted
 * label so no screen has to reason about the sign convention of the stored column.
 */
export interface SlateGameSideDto {
  side: Side;
  team: TeamDto;
  /** The line from this side's point of view: `+4.5` for the dog, `-4.5` for the favorite. */
  line: number;
  /** Display string: `+4.5`, `-3`, `PK`. */
  lineLabel: string;
  isFavorite: boolean;
  score: number | null;
  /** `true` if this side covered, `false` if it did not, `null` while pending or on a push. */
  covered: boolean | null;
}

/** Another player's pick. Only ever populated once the game is revealed. */
export interface RevealedPickDto {
  userId: string;
  displayName: string;
  selection: Side;
  result: PickResult;
  setByCommissioner: boolean;
}

export interface MyPickDto {
  selection: Side;
  /** The line on the side taken, frozen when the pick was saved. */
  pickedSpread: number;
  result: PickResult;
  pointsAwarded: number | null;
  setByCommissioner: boolean;
  updatedAt: string;
}

export interface SlateGameDto {
  id: string;
  slot: number;
  level: FootballLevel;
  neutralSite: boolean;
  venue: string | null;
  network: string | null;

  /** The contest kickoff — this is the lock deadline, frozen at selection. */
  kickoffAt: string;
  /** The home team's frozen contest line. Grading uses only this. */
  contestSpreadHome: number;
  favorite: Favorite;

  status: SlateGameStatus;
  result: AtsResult;
  gradeOverride: boolean;
  voidReason: string | null;

  /** Provenance: how this game got onto the slate. */
  selectionSource: SelectionSource;

  away: SlateGameSideDto;
  home: SlateGameSideDto;

  /**
   * Live game state, mirrored from the scoreboard feed.
   *
   * `phase` is derived rather than stored: halftime is reported by the feed as an
   * in-progress state with its own description, and collapsing it into the status enum
   * would force the lock and grading rules to learn a case that does not affect them.
   */
  live: {
    phase: 'pre' | 'in' | 'halftime' | 'final';
    /** Quarter, or period number in overtime. */
    period: number | null;
    /** Clock as the broadcast shows it: `4:21`. */
    displayClock: string | null;
    /** The feed's own words: `Q3 4:21`, `Halftime`, `Final/OT`. */
    detail: string | null;
    homeScore: number | null;
    awayScore: number | null;
    /** When the scoreboard was last read, so staleness is visible. */
    updatedAt: string | null;
    espnEventId: string | null;
  };
  locked: boolean;
  lockReason: LockReason;
  /** Whether other players' picks may be shown for this game. */
  revealed: boolean;

  myPick: MyPickDto | null;

  /** Always available, even before reveal — "4 of 8 have picked" leaks nothing. */
  pickCount: number;
  memberCount: number;
  /** Empty array unless `revealed`. The server filters this; it is never sent early. */
  otherPicks: RevealedPickDto[];
}

export interface WeekDetailDto {
  week: WeekSummaryDto;
  season: SeasonConfigDto;
  games: SlateGameDto[];

  /** Picks the signed-in user has made on this slate. */
  myPicksMade: number;
  /** Non-voided games on the slate. */
  requiredPicks: number;
  allComplete: boolean;

  /** Next game to lock, for the countdown. Null when everything is locked. */
  nextLockAt: string | null;
  /** False for a draft week, a fully locked week, or a completed week. */
  canPickAny: boolean;
  /** True when the commissioner has force-locked or force-unlocked the week. */
  lockOverrideActive: boolean;
}

export interface MyStandingDto {
  rank: number;
  tied: boolean;
  points: number;
  wins: number;
  losses: number;
  pushes: number;
  picksMade: number;
  picksAvailable: number;
}

export interface RecentWeekDto {
  weekId: string;
  weekNumber: number;
  label: string;
  points: number;
  rank: number;
  isWeeklyWinner: boolean;
  wins: number;
  losses: number;
  pushes: number;
}

/**
 * `GET /me/home` — the whole dashboard in one request. Deliberately one round trip: the
 * home screen is the first thing a phone on a stadium connection has to render, and the
 * brief is explicit that the current week's picks must not be buried behind other loads.
 */
export interface HomeDto {
  league: { id: string; name: string; slug: string; memberCount: number };
  season: SeasonConfigDto;
  /** Null before the commissioner has created any week. */
  week: WeekDetailDto | null;
  me: MyStandingDto;
  /** Top of the table plus the signed-in user, whether or not they are in the top slice. */
  standingsPreview: StandingsRowDto[];
  recentWeeks: RecentWeekDto[];
}

// ---------------------------------------------------------------------------
// Standings
// ---------------------------------------------------------------------------

export interface StandingsRowDto {
  rank: number;
  /** True when this player shares their rank with someone else. */
  tied: boolean;
  userId: string;
  displayName: string;
  avatarUrl: string | null;
  isMe: boolean;
  isCommissioner: boolean;
  wins: number;
  losses: number;
  pushes: number;
  points: number;
  picksMade: number;
  picksAvailable: number;
  /** Missed picks — graded games with no submission. Shown so absence is visible. */
  missed: number;
  winPct: number;
  /** Points in the most recently completed week, for a "last week" column. */
  lastWeekPoints: number | null;
}

export interface StandingsDto {
  season: SeasonConfigDto;
  rows: StandingsRowDto[];
  /** Weeks that have contributed at least one graded game. */
  gradedWeeks: number;
  updatedAt: string;
}

// ---------------------------------------------------------------------------
// Weekly results grid
// ---------------------------------------------------------------------------

/** One cell of the results grid: what this player did on this game. */
export interface ResultCellDto {
  slateGameId: string;
  /** Null when the player never picked, or when the game is not yet revealed. */
  selection: Side | null;
  /** The abbreviation of the side taken, for a compact grid cell. */
  selectionAbbr: string | null;
  result: PickResult;
  points: number | null;
  /** False while the game is unrevealed: the UI shows a placeholder, not a blank. */
  revealed: boolean;
  /** True when the player made no pick and the game is revealed — a genuine miss. */
  missed: boolean;
  /**
   * Whether this player has a pick saved, regardless of what it is.
   *
   * Sent even before a game reveals, and deliberately so: "who still has picks to make" is
   * the question the results grid gets opened for on a Saturday morning, and answering it
   * costs nothing that is being protected. The selection itself stays behind `revealed` —
   * this says only that a choice exists, which the league already learns from the
   * `pickCount` on every game.
   */
  hasPick: boolean;
}

export interface ResultRowDto {
  userId: string;
  displayName: string;
  isMe: boolean;
  cells: ResultCellDto[];
  weekPoints: number;
  weekWins: number;
  weekLosses: number;
  weekPushes: number;
  weekRank: number;
  tied: boolean;
  isWeeklyWinner: boolean;
}

export interface WeekResultsDto {
  week: WeekSummaryDto;
  season: SeasonConfigDto;
  games: SlateGameDto[];
  rows: ResultRowDto[];
  /** Every player tied at the top. Plural because ties are normal with five games. */
  weeklyWinners: { userId: string; displayName: string; points: number }[];
  /** True once every non-voided game is graded. */
  finalized: boolean;
}

export interface WeekListItemDto extends WeekSummaryDto {
  myPoints: number | null;
  myRank: number | null;
  myWins: number;
  myLosses: number;
  myPushes: number;
  winners: { userId: string; displayName: string; points: number }[];
  gradedGameCount: number;
}
